// ── Gmail thread picker overlay (C1449) ──
//
// Single-thread picker used by the New Task modal's Import ▾ ▸ From Gmail row to prefill
// Title/Description in place. Shares its overlay/backdrop/header/Escape/mousedown-guard
// chrome with trello-card-picker.js (C1448) via lib/import-picker-shell.js — see that
// file's header comment for why this is a body-appended overlay, not ui.js's openModal().
//
// Unlike Trello, Gmail is user-scoped (see api/src/routes/gmail.js — mounted at /api/gmail
// with no project segment), so there is no "select a board/project association" step: just
// connect, search, preview, use.
//
// Preview-before-prefill (not click-to-prefill like the Trello picker): a thread can be a
// long, noisy multi-message chain the subject/snippet alone doesn't predict, and prefill is
// a destructive overwrite of whatever the user already typed (see applyImportPrefill in
// board.js) — including its C1512 localStorage draft. The preview renders the exact plain
// text that will land in the field (escaped into a <pre>, not run through renderMarkdown())
// so what's shown is what's inserted, and a misclick can be undone with Back before
// anything is written.

import { api } from '../api.js';
import { escapeHtml, formatDate } from '../utils.js';
import { createImportPicker, notConfiguredFrom, setupNoticeHtml } from '../lib/import-picker-shell.js';
import { capToColumn as capToColumnBytes } from '../lib/import-text.js';

const esc = escapeHtml;

// (C1450) The byte-cap math itself moved to lib/import-text.js, shared with the Jira
// issue picker — it has nothing to do with Gmail specifically, only with
// tasks.description being a fixed-size DB column. This wrapper preserves the exact
// Gmail-worded notice text (and the single-arg call shape existing callers/tests use).
const TRUNCATION_NOTICE = '\n\n_…thread truncated at 60 KB. Open it in Gmail for the rest._';

// capToColumn(text) -> string, byte-safe truncation to 60,000 bytes (+ notice). See
// lib/import-text.js for the truncation mechanics (TextEncoder-measured, whole-character
// cut point).
export function capToColumn(text) {
  return capToColumnBytes(text, TRUNCATION_NOTICE);
}

// Gmail permalink — best-effort back-link, not guaranteed. The `/u/0/` account index only
// resolves to the connected mailbox when it's the browser's first signed-in Google account;
// the API has no thread URL to hand back, so this is synthesized rather than server-sourced.
function gmailPermalink(threadId) {
  return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(threadId || '')}`;
}

// buildThreadText(detail) -> string
// Print-view assembly: every message, full body, From/To/Date headers — the downstream
// coding agent parses the raw thread. Ported from openGmailWizard
// (ai/todo/server/src/client/task-board.js) minus its `### <subject>` heading: the New Task
// modal has its own Title field, same call trello-card-picker.js makes for card names.
export function buildThreadText(detail) {
  const messages = Array.isArray(detail?.messages) ? detail.messages : [];
  const body = messages.map((m) => {
    const head = [
      `**From:** ${m.from || ''}`,
      m.to ? `**To:** ${m.to}` : '',
      m.date ? `**Date:** ${m.date}` : '',
    ].filter(Boolean).join('  \n');
    return `${head}\n\n${m.body || ''}`;
  }).join('\n\n---\n\n');

  const parts = [`**Gmail thread:** ${gmailPermalink(detail?.id)}`];
  if (body) parts.push(body);
  return capToColumn(parts.join('\n\n'));
}

const GMAIL_ERRORS = {
  not_connected: 'Gmail is not connected.',
  thread_not_found: 'That thread is no longer available in Gmail.',
  // C1551 — plain-prose fallback for the two secondary call sites below (thread list/detail)
  // that keep their existing inline-banner styling; the two primary entry points (resolve,
  // connect) render the fuller setupNoticeHtml() notice with the actual missing var names.
  gmail_not_configured: "Gmail import isn't configured on this server yet — see GMAIL_SETUP.md.",
};

// api.js sets err.message = body.error, and routes/gmail.js returns machine tokens
// ('not_connected', 'thread_not_found') as that error field — showing err.message directly
// would print the token itself. Map known tokens to prose; leave everything else to the
// caller's fallback.
function gmailErrorMessage(err, fallback) {
  const code = err && err.body && err.body.error;
  if (code && GMAIL_ERRORS[code]) return GMAIL_ERRORS[code];
  // An expired/revoked Google refresh token surfaces as a 5xx from sendServerError(), NOT
  // as not_connected — routes/gmail.js has no invalid_grant branch to distinguish it from
  // any other server error. gmail_not_configured (C1551) is also >= 500 (503) but handled
  // separately via notConfiguredFrom() at each call site — checked before this fallback
  // ever runs, so it never reaches "connection may have expired" wording.
  if (err && err.status >= 500) return 'Gmail request failed — your connection may have expired.';
  return fallback;
}

// C1551: a not-configured 503 isn't reconnectable — there is no connection to reconnect,
// the server itself is missing GMAIL_* env vars. Offering "Reconnect Gmail" there would be
// a dead-end loop (auth/initiate just 503s again).
function isReconnectable(err) {
  return !!(err && err.status >= 500) && !notConfiguredFrom(err);
}

const SORT_DEFAULT_DIR = { subject: 'asc', from: 'asc', updated: 'desc', message_count: 'desc' };
const SORT_LABELS = { subject: 'Subject', from: 'From', updated: 'Updated', message_count: '#' };

function sortThreads(threads, key, dir) {
  const mul = dir === 'asc' ? 1 : -1;
  const arr = threads.slice();
  arr.sort((a, b) => {
    if (key === 'updated') {
      const av = Date.parse(a.updated || a.date || '');
      const bv = Date.parse(b.updated || b.date || '');
      const aNaN = Number.isNaN(av), bNaN = Number.isNaN(bv);
      if (aNaN && bNaN) return 0;
      if (aNaN) return 1; // unparseable dates sort last regardless of direction
      if (bNaN) return -1;
      return (av - bv) * mul;
    }
    if (key === 'message_count') {
      return ((a.message_count || 0) - (b.message_count || 0)) * mul;
    }
    return String(a[key] || '').toLowerCase().localeCompare(String(b[key] || '').toLowerCase()) * mul;
  });
  return arr;
}

function headHtml(sortKey, sortDir) {
  return Object.keys(SORT_LABELS).map((key) => {
    const active = key === sortKey;
    const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    return `<button type="button" class="gmail-th${active ? ' is-active' : ''}" data-sort="${key}" aria-sort="${active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}">${esc(SORT_LABELS[key])}${arrow}</button>`;
  }).join('');
}

function threadRowsHtml(threads) {
  if (!threads.length) return '';
  return threads.map((t) => `
    <div class="gmail-thread-row" data-id="${esc(t.id)}" tabindex="0" role="button">
      <span class="gmail-thread-subject">${esc(t.subject || '(no subject)')}</span>
      <span class="gmail-thread-from">${esc(t.from || '')}</span>
      <span class="gmail-thread-updated">${esc(formatDate(t.updated || t.date))}</span>
      <span class="gmail-thread-count">${t.message_count || 1}</span>
      ${t.snippet ? `<span class="gmail-thread-snippet">${esc(t.snippet)}</span>` : ''}
    </div>`).join('');
}

// openGmailThreadPicker({ onPick }) -> void
//   onPick({ title, description }) — called once, right before the overlay closes itself.
export function openGmailThreadPicker({ onPick } = {}) {
  const ui = createImportPicker({ title: 'Import from Gmail' });
  const { body, setBody, statusHtml, errorHtml, showInlineError, onWindowFocusOnce, close } = ui;

  const state = { onPick, query: '', threads: [], sortKey: 'updated', sortDir: 'desc' };
  let searchTimer = null;
  let latestSeq = 0;

  // ── Step 0: resolve connection ──
  async function renderResolveStep() {
    setBody(statusHtml('Checking connection…'));
    let statusRes;
    try {
      statusRes = await api.get('/gmail/status');
    } catch (err) {
      if (ui.destroyed) return;
      const notConfigured = notConfiguredFrom(err);
      setBody(notConfigured ? setupNoticeHtml(notConfigured) : errorHtml(gmailErrorMessage(err, 'Failed to check Gmail connection.')));
      return;
    }
    if (ui.destroyed) return;

    if (!statusRes.connected) { renderConnectStep(); return; }
    renderSearchStep(true);
  }

  // ── Step 1: connect ──
  function renderConnectStep() {
    setBody(`
      <p style="margin:0 0 12px">Connect your Gmail account to import a thread.</p>
      <p style="margin:0 0 16px;font-size:.85rem;color:var(--c-overlay1)">Opens Gmail sign-in in a new tab — this window stays open.</p>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="gmail-picker-cancel">Cancel</button>
        <button class="btn btn-primary" id="gmail-picker-connect">Connect Gmail</button>
      </div>`);

    body.querySelector('#gmail-picker-cancel').addEventListener('click', close);
    body.querySelector('#gmail-picker-connect').addEventListener('click', async () => {
      const btn = body.querySelector('#gmail-picker-connect');
      btn.disabled = true; btn.textContent = 'Opening…';
      // Open the tab synchronously, inside the click gesture — Safari/Firefox block
      // window.open() called after an await (the user-gesture chain is already broken by
      // then), so the real navigation target is assigned once the URL comes back.
      const w = window.open('', '_blank');
      try {
        const { url } = await api.get('/gmail/auth/initiate');
        if (ui.destroyed) { w?.close(); return; }
        if (w) { w.opener = null; w.location = url; }
        renderWaitingStep(url);
      } catch (err) {
        w?.close();
        if (ui.destroyed) return;
        const notConfigured = notConfiguredFrom(err);
        if (notConfigured) { setBody(setupNoticeHtml(notConfigured)); return; }
        btn.disabled = false; btn.textContent = 'Connect Gmail';
        showInlineError(gmailErrorMessage(err, 'Failed to start Gmail authorization.'));
      }
    });
  }

  function renderWaitingStep(url) {
    setBody(`
      <p style="margin:0 0 12px">Finish authorizing in the other tab, then come back here.</p>
      <p style="margin:0 0 16px;font-size:.85rem"><a href="${esc(url)}" target="_blank" rel="noopener">Reopen the Gmail authorization page</a></p>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="gmail-picker-cancel">Cancel</button>
        <button class="btn btn-primary" id="gmail-picker-retry">I've connected — Continue</button>
      </div>`);
    body.querySelector('#gmail-picker-cancel').addEventListener('click', close);
    body.querySelector('#gmail-picker-retry').addEventListener('click', renderResolveStep);

    // Auto-retry once the user tabs back to this window.
    onWindowFocusOnce(renderResolveStep);
  }

  // ── Step 2: search + sortable list ──
  // fetchOnMount: true for the initial entry from Step 0; false when returning here via
  // the preview step's Back button, which repaints cached state.threads with no refetch.
  function renderSearchStep(fetchOnMount) {
    setBody(`
      <div class="form-group">
        <input class="form-input" id="gmail-picker-search" type="search" autocomplete="off"
               placeholder="Search Gmail (e.g. from:alice invoice)" value="${esc(state.query)}">
      </div>
      <div class="gmail-thread-table">
        <div class="gmail-thread-head" id="gmail-picker-head">${headHtml(state.sortKey, state.sortDir)}</div>
        <div class="gmail-thread-list" id="gmail-picker-list"></div>
      </div>`);

    const searchInput = body.querySelector('#gmail-picker-search');
    const head = body.querySelector('#gmail-picker-head');

    searchInput.addEventListener('input', () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => fetchThreads(searchInput.value.trim()), 300);
    });

    head.addEventListener('click', (e) => {
      const th = e.target.closest('.gmail-th');
      if (!th) return;
      const key = th.dataset.sort;
      if (state.sortKey === key) {
        state.sortDir = state.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        state.sortKey = key;
        state.sortDir = SORT_DEFAULT_DIR[key] || 'asc';
      }
      head.innerHTML = headHtml(state.sortKey, state.sortDir);
      renderRows();
    });

    body.querySelector('#gmail-picker-list').addEventListener('click', (e) => {
      const list = body.querySelector('#gmail-picker-list');
      const row = e.target.closest('.gmail-thread-row');
      if (!row || list.classList.contains('is-busy')) return;
      pickThread(row.dataset.id, row);
    });

    if (fetchOnMount) fetchThreads(state.query);
    else renderRows();
  }

  function renderRows() {
    const list = body.querySelector('#gmail-picker-list');
    if (!list) return; // step moved on already
    if (!state.threads.length) {
      list.innerHTML = statusHtml(state.query ? 'No threads match that search.' : 'No recent threads found.');
      return;
    }
    list.innerHTML = threadRowsHtml(sortThreads(state.threads, state.sortKey, state.sortDir));
  }

  async function fetchThreads(q) {
    const seq = ++latestSeq;
    const list = body.querySelector('#gmail-picker-list');
    if (list) list.innerHTML = statusHtml('Searching…');

    let threads;
    try {
      threads = await api.get('/gmail/threads?limit=50' + (q ? '&q=' + encodeURIComponent(q) : ''));
    } catch (err) {
      if (ui.destroyed || seq !== latestSeq) return;
      if (err && err.body && err.body.error === 'not_connected') { renderConnectStep(); return; }
      const freshList = body.querySelector('#gmail-picker-list');
      if (freshList) {
        freshList.innerHTML = errorHtml(gmailErrorMessage(err, 'Failed to load threads.')) +
          (isReconnectable(err) ? `<div style="text-align:center;padding:0 0 12px"><button class="btn btn-ghost btn-sm" id="gmail-picker-reconnect">Reconnect Gmail</button></div>` : '');
        freshList.querySelector('#gmail-picker-reconnect')?.addEventListener('click', renderConnectStep);
      }
      return;
    }
    if (ui.destroyed || seq !== latestSeq) return;

    state.query = q;
    // NOTE: /gmail/threads returns a bare JSON array, not { threads }.
    state.threads = Array.isArray(threads) ? threads : [];
    renderRows();
  }

  async function pickThread(threadId, row) {
    clearTimeout(searchTimer); searchTimer = null;
    latestSeq++; // discard any /threads response still on the wire — it must not repaint under this pick
    const list = body.querySelector('#gmail-picker-list');
    list.classList.add('is-busy');
    row.classList.add('is-picking');

    let detail;
    try {
      detail = await api.get('/gmail/threads/' + encodeURIComponent(threadId));
    } catch (err) {
      if (ui.destroyed) return;
      list.classList.remove('is-busy');
      row.classList.remove('is-picking');
      showInlineError(gmailErrorMessage(err, 'Failed to load thread.'));
      return;
    }
    if (ui.destroyed) return;

    renderPreviewStep(detail);
  }

  // ── Step 3: preview ──
  function renderPreviewStep(detail) {
    const messages = Array.isArray(detail.messages) ? detail.messages : [];
    const description = buildThreadText(detail); // exactly what "Use this thread" will insert
    const sizeBytes = new TextEncoder().encode(description).length;
    const truncated = description.includes('thread truncated at 60 KB');
    const subLine = `${messages.length} message${messages.length === 1 ? '' : 's'} · ${(sizeBytes / 1024).toFixed(1)} KB${truncated ? ' (truncated)' : ''}`;

    const msgsHtml = messages.length
      ? messages.map((m) => {
          const headLines = [`From: ${m.from || ''}`, m.to ? `To: ${m.to}` : '', m.date ? `Date: ${m.date}` : ''].filter(Boolean);
          return `<div class="gmail-msg">
            <div class="gmail-msg-head">${headLines.map(esc).join('<br>')}</div>
            <pre class="gmail-msg-body">${esc(m.body || '')}</pre>
          </div>`;
        }).join('')
      : statusHtml('This thread has no readable message text — only the subject will be imported.');

    setBody(`
      <div class="import-picker-preview-head">
        <div class="import-picker-preview-title">${esc(detail.subject || '(no subject)')}</div>
        <div class="import-picker-preview-sub">${esc(subLine)}</div>
      </div>
      <div class="gmail-thread-preview">${msgsHtml}</div>
      <div class="modal-footer">
        <button class="btn btn-ghost" id="gmail-picker-back">Back</button>
        <button class="btn btn-primary" id="gmail-picker-use">Use this thread</button>
      </div>`);

    body.querySelector('#gmail-picker-back').addEventListener('click', () => renderSearchStep(false));
    body.querySelector('#gmail-picker-use').addEventListener('click', () => {
      const payload = { title: detail.subject || '', description };
      close();
      if (state.onPick) state.onPick(payload);
    });
  }

  renderResolveStep();
}
